import React from 'react';
import { useNavigate } from 'react-router-dom';

import { usePageStore } from '@client/store/pageStore/context/pageContext';
import aggregateSpectrograms from '@client/utils/aggregateSpectrograms';
import i18n from '@client/utils/i18n';
import lediumApi from '@client/utils/lediumApi';
import { SpectrogramPoint } from '@ledium/types';
import { AutocompleteBordered, AutocompleteOptionWithChart, computeSpectralSimilarity } from '@ledium/ui-common';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Alert, AlertTitle, Box, Button, Container, Grid, Paper, TextField, Typography } from '@mui/material';

interface LightRecipeItem {
    id: string;
    name: string;
    description?: string;
    spectrogram: SpectrogramPoint[];
}

interface SpectrumMixOption {
    id: string;
    name: string;
    description?: string;
    zoneId?: string;
    linkedReason?: string;
    linked?: { projectId: string; projectName: string } | null;
    lightChannels: Array<{ spectrogram: SpectrogramPoint[]; intensityPercent: number }>;
    lightRecipe?: { spectrogram?: SpectrogramPoint[] } | null;
    similarity?: number;
}

interface FilterByLightRecipeResponse {
    lightRecipe: LightRecipeItem;
    spectrumMixList: SpectrumMixOption[];
    autoMixSimilarity: number;
}

interface ProjectEditorSpectrumMixSelectorProps {
    lightRecipeId: string;
    spectrumMixId: string | null;
    onSpectrumMixSelect: (lightRecipeId: string, spectrumMixId: string | null) => void;
    readOnly: boolean;
    projectId: unknown;
}

const ProjectEditorSpectrumMixSelector = ({
    lightRecipeId,
    spectrumMixId,
    onSpectrumMixSelect,
    readOnly,
    projectId
}: ProjectEditorSpectrumMixSelectorProps) => {
    const navigate = useNavigate();
    const {
        pageState: { formData }
    } = usePageStore();
    const [lightRecipe, setLightRecipe] = React.useState<LightRecipeItem | null>(null);
    const [spectrumMixListForRecipe, setSpectrumMixListForRecipe] = React.useState<SpectrumMixOption[]>([]);
    const [matchedSpectrumMixList, setMatchedSpectrumMixList] = React.useState<SpectrumMixOption[]>([]);
    const [selectedSpectrumMix, setSelectedSpectrumMix] = React.useState<SpectrumMixOption | null>(null);
    const [autoMixSimilarity, setAutoMixSimilarity] = React.useState<number>(0);

    React.useEffect(() => {
        lediumApi.get<FilterByLightRecipeResponse>(`spectrumMixes/filterByLightRecipe/${lightRecipeId}?zoneId=${formData.zoneId}`).then((data) => {
            if (!data) {
                return;
            }
            setSpectrumMixListForRecipe(data.spectrumMixList);
            setLightRecipe(data.lightRecipe);
            setAutoMixSimilarity(data.autoMixSimilarity);
        });
    }, [lightRecipeId, formData.zoneId]);

    React.useEffect(() => {
        if (selectedSpectrumMix && selectedSpectrumMix.id !== spectrumMixId) {
            onSpectrumMixSelect(lightRecipeId, selectedSpectrumMix.id);
        }
    }, [selectedSpectrumMix, onSpectrumMixSelect, lightRecipeId, spectrumMixId]);

    React.useEffect(() => {
        if (spectrumMixId) {
            const selected = matchedSpectrumMixList.find((e) => e.id === spectrumMixId);
            if (selected) {
                setSelectedSpectrumMix(selected);
            }
        }
    }, [matchedSpectrumMixList, spectrumMixId]);

    React.useEffect(() => {
        const matched = spectrumMixListForRecipe
            .filter((e) => e.zoneId === formData.zoneId)
            .map((e) => ({
                ...e,
                similarity: lightRecipe
                    ? computeSpectralSimilarity(lightRecipe.spectrogram, aggregateSpectrograms(e.lightChannels, e.lightRecipe), 'irradiance')
                    : undefined
            }))
            .sort((a, b) => (b.similarity ?? 0) - (a.similarity ?? 0));
        setMatchedSpectrumMixList(matched);
        if (matched.length === 1 && !spectrumMixId) {
            setSelectedSpectrumMix(matched[0]);
        }
    }, [formData.zoneId, spectrumMixListForRecipe, lightRecipe, spectrumMixId]);

    if (!lightRecipe) {
        return null;
    }

    function onClickSpectrumMixAlert() {
        navigate('/spectrum-mixes');
    }

    async function onAutoCreate() {
        const result = await lediumApi.post<SpectrumMixOption>('spectrumMixes/autoCreate', {
            lightRecipeId,
            zoneId: formData.zoneId
        });
        if (!result) {
            return;
        }
        setMatchedSpectrumMixList((prev) => [{ ...result, similarity: autoMixSimilarity }, ...prev]);
        onSpectrumMixSelect(lightRecipeId, result.id);
        setSelectedSpectrumMix({ ...result, similarity: autoMixSimilarity });
    }

    function getOptionLabel(option: SpectrumMixOption) {
        const similarity = option.similarity === undefined ? '' : ` (Spectral similarity: ${option.similarity}%)`;
        return option.name + (option.description ? ` (${option.description})` : '') + similarity;
    }

    function isOptionEqualToValue(option: SpectrumMixOption, value: SpectrumMixOption) {
        return option.id === value.id;
    }

    function getOptionDisabled(option: SpectrumMixOption) {
        if (option.linked && option.linked.projectId !== projectId) {
            option.linkedReason = i18n('project_linked_spectrum_mix', option.linked.projectName) as string;
            return true;
        }

        return false;
    }

    function onChange(_event: React.SyntheticEvent, item: SpectrumMixOption | null) {
        setSelectedSpectrumMix(item);
        if (item === null && spectrumMixId !== null) {
            onSpectrumMixSelect(lightRecipeId, null);
        }
    }

    function renderInput(params: object) {
        return <TextField {...(params as object)} label={i18n('project_editor_select_spectrum_mix', lightRecipe!.name)} variant="outlined" />;
    }

    function renderOption({ key, ...props }: React.HTMLAttributes<HTMLLIElement> & { key: React.Key }, option: SpectrumMixOption) {
        const optionWithSimilarity = {
            ...option,
            additionalItemListInfo: option.similarity === undefined ? undefined : `Spectral similarity: ${option.similarity}%`
        };
        return (
            <li key={key} {...props}>
                <AutocompleteOptionWithChart
                    option={optionWithSimilarity}
                    spectrogramData={aggregateSpectrograms(option.lightChannels, option.lightRecipe)}
                    size="small"
                />
            </li>
        );
    }

    function renderSuggestion() {
        if (!autoMixSimilarity) {
            return null;
        }
        if (selectedSpectrumMix && selectedSpectrumMix.similarity !== undefined && selectedSpectrumMix.similarity >= autoMixSimilarity) {
            return null;
        }

        const matchingMix = matchedSpectrumMixList.find((m) => m.similarity === autoMixSimilarity);
        if (matchingMix && matchingMix.id !== selectedSpectrumMix?.id) {
            return (
                <Alert severity="info" sx={{ mt: 1 }}>
                    Better match available — <strong>{matchingMix.name}</strong> achieves <strong>{autoMixSimilarity}%</strong> similarity
                </Alert>
            );
        }

        const bestSimilarity = matchedSpectrumMixList[0]?.similarity ?? 0;
        if (autoMixSimilarity > bestSimilarity) {
            const message =
                matchedSpectrumMixList.length === 0 ? (
                    <>
                        An auto-generated mix can achieve <strong>{autoMixSimilarity}%</strong> similarity
                    </>
                ) : (
                    <>
                        A better match is achievable — <strong>{autoMixSimilarity}%</strong> similarity with an auto-generated mix
                    </>
                );
            return (
                <Alert
                    severity="info"
                    sx={{ mt: 1 }}
                    action={
                        <Button variant="outlined" color="inherit" size="small" onClick={onAutoCreate}>
                            Auto-create mix
                        </Button>
                    }
                >
                    {message}
                </Alert>
            );
        }

        return null;
    }

    const selectorOrAlert = matchedSpectrumMixList.length ? (
        <AutocompleteBordered
            value={selectedSpectrumMix}
            options={matchedSpectrumMixList}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            getOptionDisabled={getOptionDisabled}
            onChange={onChange}
            renderInput={renderInput}
            renderOption={renderOption}
        />
    ) : (
        <Alert severity="error">
            <AlertTitle>Error</AlertTitle>
            There is no Spectrum Mix for the <b>{lightRecipe.name}</b> Light Recipe.
            <Box>
                <Button style={{ marginTop: '10px' }} variant="contained" onClick={onClickSpectrumMixAlert}>
                    {i18n('add_new_spectrum_mix_button')}
                </Button>
            </Box>
        </Alert>
    );

    return (
        <Paper sx={{ pb: 2 }} elevation={9}>
            <Container>
                <Grid container spacing={3}>
                    <Grid item xs={5}>
                        <Typography variant="body2">{i18n('original_light_recipe')}</Typography>
                        <AutocompleteOptionWithChart option={lightRecipe} spectrogramData={lightRecipe.spectrogram} size="small" />
                    </Grid>
                    <Grid item xs={1}>
                        {selectedSpectrumMix ? <ArrowForwardIcon /> : null}
                    </Grid>
                    <Grid item xs={6}>
                        {selectedSpectrumMix ? (
                            <>
                                <Typography variant="body2">{i18n('mixed_spectrum')}</Typography>
                                <AutocompleteOptionWithChart
                                    option={selectedSpectrumMix}
                                    spectrogramData={aggregateSpectrograms(selectedSpectrumMix.lightChannels)}
                                    size="small"
                                />
                            </>
                        ) : null}
                    </Grid>
                    <Grid item xs={12}>
                        {readOnly ? null : (
                            <>
                                {selectorOrAlert}
                                {renderSuggestion()}
                            </>
                        )}
                    </Grid>
                </Grid>
            </Container>
        </Paper>
    );
};

export default ProjectEditorSpectrumMixSelector;
